/**
 * 二值形态学（开/腐蚀/膨胀）与盒式模糊羽化 —— 逐行对拍 climb-core/src/morph.rs
 * 滑窗均为边缘钳制（窗口截短），输出 0/255。
 */

function hpass(
  mask: Uint8Array,
  w: number,
  h: number,
  r: number,
  isDilate: boolean,
): Uint8Array {
  const out = new Uint8Array(w * h);
  if (w === 0) return out;
  for (let y = 0; y < h; y++) {
    const row = y * w;
    let cnt = 0;
    const initHi = Math.min(r, w - 1);
    for (let x = 0; x <= initHi; x++) {
      if (mask[row + x] !== 0) cnt++;
    }
    for (let x = 0; x < w; x++) {
      const lo = Math.max(x - r, 0);
      const hi = Math.min(x + r, w - 1);
      const window = hi - lo + 1;
      const set = isDilate ? cnt > 0 : cnt === window;
      out[row + x] = set ? 255 : 0;
      const nlo = Math.max(x + 1 - r, 0);
      if (nlo > lo && mask[row + lo] !== 0) cnt--;
      const nhi = Math.min(x + 1 + r, w - 1);
      if (nhi > hi && mask[row + nhi] !== 0) cnt++;
    }
  }
  return out;
}

function vpass(
  mask: Uint8Array,
  w: number,
  h: number,
  r: number,
  isDilate: boolean,
): Uint8Array {
  const out = new Uint8Array(w * h);
  if (h === 0) return out;
  for (let x = 0; x < w; x++) {
    let cnt = 0;
    const initHi = Math.min(r, h - 1);
    for (let y = 0; y <= initHi; y++) {
      if (mask[y * w + x] !== 0) cnt++;
    }
    for (let y = 0; y < h; y++) {
      const lo = Math.max(y - r, 0);
      const hi = Math.min(y + r, h - 1);
      const window = hi - lo + 1;
      const set = isDilate ? cnt > 0 : cnt === window;
      out[y * w + x] = set ? 255 : 0;
      const nlo = Math.max(y + 1 - r, 0);
      if (nlo > lo && mask[lo * w + x] !== 0) cnt--;
      const nhi = Math.min(y + 1 + r, h - 1);
      if (nhi > hi && mask[nhi * w + x] !== 0) cnt++;
    }
  }
  return out;
}

export function erode(mask: Uint8Array, w: number, h: number, r: number): Uint8Array {
  if (r === 0) return mask.slice();
  return vpass(hpass(mask, w, h, r, false), w, h, r, false);
}

export function dilate(mask: Uint8Array, w: number, h: number, r: number): Uint8Array {
  if (r === 0) return mask.slice();
  return vpass(hpass(mask, w, h, r, true), w, h, r, true);
}

/** 形态学开：先腐蚀后膨胀，去除细小噪点 */
export function open(mask: Uint8Array, w: number, h: number, r: number): Uint8Array {
  if (r === 0) return mask.slice();
  return dilate(erode(mask, w, h, r), w, h, r);
}

/** 盒式模糊（f32 精度、边缘钳制），掩码羽化用。对拍 morph.rs:90-137 */
export function blur(mask: Uint8Array, w: number, h: number, radius: number): Uint8Array {
  const r = Math.max(Math.ceil(Math.max(radius, 0.5)), 1);
  const n = w * h;
  let f = new Float32Array(n);
  for (let i = 0; i < n; i++) f[i] = mask[i];
  const tmp = new Float32Array(n);
  // 水平
  for (let y = 0; y < h; y++) {
    const row = y * w;
    let sum = 0;
    const initHi = Math.min(r, Math.max(w - 1, 0));
    for (let x = 0; x <= initHi; x++) {
      sum += f[row + Math.min(x, w - 1)];
    }
    for (let x = 0; x < w; x++) {
      const lo = Math.max(x - r, 0);
      const hi = Math.min(x + r, w - 1);
      tmp[row + x] = sum / (hi - lo + 1);
      const nlo = Math.max(x + 1 - r, 0);
      if (nlo > lo) sum -= f[row + lo];
      const nhi = Math.min(x + 1 + r, w - 1);
      if (nhi > hi) sum += f[row + nhi];
    }
  }
  // 垂直
  for (let x = 0; x < w; x++) {
    let sum = 0;
    const initHi = Math.min(r, Math.max(h - 1, 0));
    for (let y = 0; y <= initHi; y++) {
      sum += tmp[Math.min(y, h - 1) * w + x];
    }
    for (let y = 0; y < h; y++) {
      const lo = Math.max(y - r, 0);
      const hi = Math.min(y + r, h - 1);
      f[y * w + x] = sum / (hi - lo + 1);
      const nlo = Math.max(y + 1 - r, 0);
      if (nlo > lo) sum -= tmp[lo * w + x];
      const nhi = Math.min(y + 1 + r, h - 1);
      if (nhi > hi) sum += tmp[nhi * w + x];
    }
  }
  const out = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    out[i] = Math.min(Math.max(Math.trunc(f[i]), 0), 255);
  }
  return out;
}
